import { useState, useEffect, useCallback, useRef } from 'react';
import { SEC, severityColor } from './SecurityTheme';
import { getAllSecurityAlerts, SecurityAlertItem } from '../../services/api';
import { format, parseISO } from 'date-fns';

interface AlertFeedProps {
  compact?: boolean;
  maxItems?: number;
  autoRefreshMs?: number;
  clientId?: string;
}

export default function AlertFeed({ compact = false, maxItems = 50, autoRefreshMs = 30000, clientId }: AlertFeedProps) {
  const [alerts, setAlerts] = useState<SecurityAlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await getAllSecurityAlerts({ limit: maxItems });
      if (data.success) {
        const filtered = clientId ? data.alerts.filter(a => a.clientId === clientId) : data.alerts;
        setAlerts(filtered);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [maxItems, clientId]);

  useEffect(() => {
    fetchAlerts();
    if (autoRefreshMs > 0) {
      intervalRef.current = setInterval(fetchAlerts, autoRefreshMs);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchAlerts, autoRefreshMs]);

  const severityBadge = (severity: string) => {
    const color = severityColor(severity);
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
        style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
      >
        {severity}
      </span>
    );
  };

  const statusDot = (status: string) => {
    const colors: Record<string, string> = {
      new: SEC.red,
      acknowledged: SEC.amber,
      investigating: SEC.amber,
      resolved: SEC.green,
      false_positive: SEC.t3,
    };
    const c = colors[status] || SEC.t3;
    return (
      <span
        className="inline-block w-2 h-2 rounded-full mr-1.5"
        style={{ background: c, boxShadow: status === 'new' ? `0 0 6px ${c}` : 'none' }}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" style={{ color: SEC.t3 }}>
        <div
          className="animate-spin rounded-full h-5 w-5 border-2 mr-2"
          style={{ borderColor: SEC.t3, borderTopColor: SEC.cyan }}
        />
        Loading alerts...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6" style={{ color: SEC.t3 }}>
        <p className="text-sm">{error}</p>
        <button
          onClick={fetchAlerts}
          className="mt-2 text-xs px-3 py-1 rounded"
          style={{ color: SEC.cyan, border: `1px solid ${SEC.cyanDim}` }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="text-center py-6" style={{ color: SEC.t3 }}>
        <p className="text-sm">No security alerts</p>
      </div>
    );
  }

  return (
    <div
      className="overflow-y-auto"
      style={{ maxHeight: compact ? '320px' : '600px' }}
    >
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className="px-3 py-2.5 border-b transition-colors hover:bg-white/[0.02]"
          style={{ borderColor: SEC.border }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                {severityBadge(alert.severity)}
                {!compact && (
                  <span className="text-xs font-medium truncate" style={{ color: SEC.t2 }}>
                    {alert.clientName}
                  </span>
                )}
              </div>
              <p
                className="text-sm truncate"
                style={{ color: SEC.t1 }}
                title={alert.title}
              >
                {alert.title}
              </p>
              {!compact && alert.summary && (
                <p className="text-xs mt-0.5 line-clamp-1" style={{ color: SEC.t3 }}>
                  {alert.summary}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end shrink-0">
              <span className="flex items-center text-[10px]" style={{ color: SEC.t3 }}>
                {statusDot(alert.status)}
                {alert.status}
              </span>
              <span className="text-[10px] mt-0.5" style={{ color: SEC.t4 }}>
                {formatAlertTime(alert.createdDate)}
              </span>
            </div>
          </div>
          {!compact && alert.iocTypes.length > 0 && (
            <div className="flex gap-1 mt-1">
              {alert.iocTypes.slice(0, 3).map((ioc, i) => (
                <span
                  key={i}
                  className="text-[9px] px-1.5 py-0.5 rounded"
                  style={{ background: SEC.cyanGlow, color: SEC.cyanDim }}
                >
                  {ioc}
                </span>
              ))}
              {alert.iocTypes.length > 3 && (
                <span className="text-[9px]" style={{ color: SEC.t4 }}>
                  +{alert.iocTypes.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatAlertTime(dateStr: string): string {
  try {
    const d = parseISO(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return format(d, 'MMM d HH:mm');
  } catch {
    return dateStr;
  }
}
